import { useState } from 'react'
import { Plus, Edit2, Trash2, X, Database, HardDrive, CheckCircle, AlertTriangle, Download } from 'lucide-react'

export default function DataBackupRecoveryPage() {
  const [backups, setBackups] = useState([
    {
      id: 1,
      name: 'Daily Full Backup',
      type: 'full',
      schedule: 'Daily at 2:00 AM',
      lastRun: new Date(Date.now() - 86400000).toLocaleDateString(),
      nextRun: new Date(Date.now() + 3600000).toLocaleDateString(),
      size: '2.4 TB',
      status: 'success',
      retentionDays: 30
    },
    {
      id: 2,
      name: 'Hourly Incremental Backup',
      type: 'incremental',
      schedule: 'Every hour',
      lastRun: new Date().toLocaleDateString(),
      nextRun: new Date(Date.now() + 3600000).toLocaleDateString(),
      size: '150 GB',
      status: 'success',
      retentionDays: 7
    }
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingBackup, setEditingBackup] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    type: 'incremental',
    schedule: 'daily',
    retentionDays: 30
  })

  const handleCreateClick = () => {
    setEditingBackup(null)
    setFormData({ name: '', type: 'incremental', schedule: 'daily', retentionDays: 30 })
    setShowModal(true)
  }

  const handleEditBackup = (backup) => {
    setEditingBackup(backup)
    setFormData({
      name: backup.name,
      type: backup.type,
      schedule: backup.schedule.toLowerCase().includes('daily') ? 'daily' : 'hourly',
      retentionDays: backup.retentionDays
    })
    setShowModal(true)
  }

  const handleSaveBackup = () => {
    if (!formData.name) {
      alert('Please fill in all required fields')
      return
    }

    if (editingBackup) {
      setBackups(backups.map(b =>
        b.id === editingBackup.id
          ? { ...b, ...formData }
          : b
      ))
    } else {
      const newBackup = {
        id: Date.now(),
        ...formData,
        schedule: formData.schedule === 'daily' ? 'Daily at 2:00 AM' : 'Every hour',
        lastRun: new Date().toLocaleDateString(),
        nextRun: new Date(Date.now() + 3600000).toLocaleDateString(),
        size: '0 GB',
        status: 'pending'
      }
      setBackups([...backups, newBackup])
    }

    setShowModal(false)
  }

  const handleDeleteBackup = (id) => {
    if (confirm('Are you sure you want to delete this backup policy?')) {
      setBackups(backups.filter(b => b.id !== id))
    }
  }

  const getStatusIcon = (status) => {
    return status === 'success' ? (
      <CheckCircle className="w-5 h-5 text-green-400" />
    ) : (
      <AlertTriangle className="w-5 h-5 text-yellow-400" />
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Data Backup & Recovery</h1>
          </div>
          <p className="text-slate-400">Manage backup policies, schedules, and recovery operations</p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active Backups</div>
            <div className="text-3xl font-bold text-blue-400">{backups.length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Storage</div>
            <div className="text-3xl font-bold text-cyan-400">{backups.reduce((sum, b) => {
              const size = parseInt(b.size)
              return sum + size
            }, 0)} GB</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Successful Backups</div>
            <div className="text-3xl font-bold text-green-400">{backups.filter(b => b.status === 'success').length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg Retention</div>
            <div className="text-3xl font-bold text-yellow-400">{Math.round(backups.reduce((sum, b) => sum + b.retentionDays, 0) / backups.length)} days</div>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreateClick}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Create Backup Policy
        </button>

        {/* Backups List */}
        <div className="space-y-4">
          {backups.map((backup) => (
            <div
              key={backup.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <HardDrive className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{backup.name}</h3>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold text-blue-400 bg-blue-600/20">
                      {backup.type.toUpperCase()}
                    </span>
                    {getStatusIcon(backup.status)}
                  </div>
                  <p className="text-slate-400 mb-4">{backup.schedule}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Backup Size:</span>
                      <span className="text-white ml-2">{backup.size}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Last Run:</span>
                      <span className="text-white ml-2">{backup.lastRun}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Next Run:</span>
                      <span className="text-white ml-2">{backup.nextRun}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Retention:</span>
                      <span className="text-white ml-2">{backup.retentionDays} days</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="p-2 bg-green-600/20 text-green-400 hover:bg-green-600/30 rounded-lg transition-all"
                    title="Restore Backup"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleEditBackup(backup)}
                    className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteBackup(backup.id)}
                    className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingBackup ? 'Edit Backup Policy' : 'Create Backup Policy'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-700 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Policy Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., Daily Full Backup"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Backup Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="full">Full</option>
                      <option value="incremental">Incremental</option>
                      <option value="differential">Differential</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Schedule</label>
                    <select
                      value={formData.schedule}
                      onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Retention Period (Days)</label>
                  <input
                    type="number"
                    value={formData.retentionDays}
                    onChange={(e) => setFormData({ ...formData, retentionDays: parseInt(e.target.value) })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    min="1"
                    max="365"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBackup}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Save Policy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
